/* =========================================================
   CRYSTAL ATELIER — SWIPE DISCOVERY
   ---------------------------------------------------------
   Architecture is intentionally modular so that porting to
   Shopify requires NO changes to this file. All it needs is:
     1. A mount point  #swipe-root  containing the same views
     2. A <script id="swipe-data" type="application/json">
        with the product array (Liquid-rendered in Shopify)
     3. Optional: window.SHOPIFY_CART_ADD hook
   ========================================================= */

(function () {
  'use strict';

  /* ---------- CONFIG ---------- */
  const STORAGE_KEY = 'crystal_atelier_swipe_v1';
  const SWIPE_THRESHOLD = 110;        // px of drag to trigger a swipe
  const VELOCITY_THRESHOLD = 0.5;     // px/ms
  const MAX_ROTATION = 16;            // degrees at edge
  const STACK_SIZE = 3;               // cards rendered at once

  /* Product selection strategy */
  const SEEN_EXPIRY_DAYS = 3;         // forget "seen" history after this many days
  const DEFAULT_DECK_SIZE = 24;       // fallback if data-deck-size missing
  // How long to remember a product as "already shown" so it doesn't recur.
  // After this window, products become eligible to appear again.

  /* ---------- STATE ---------- */
  const state = {
    allProducts: [],   // full catalog from JSON (never mutated)
    products: [],      // the shuffled, filtered deck for this session
    index: 0,
    liked: [],         // products swiped right this session
    passed: [],        // products swiped left this session
    seenIds: new Set(),// all product ids seen across sessions (persisted)
    inCartIds: new Set(), // product ids user has added to cart (persisted)
    likeCounts: {},    // {product_id: count} from Supabase, used for "X people love this"
    likedThisSession: new Set() // product ids the user already incremented this session (avoid double count)
  };

  /* ---------- DOM ---------- */
  const root = document.getElementById('swipe-root');
  if (!root) return;

  const views = {
    intro:   root.querySelector('[data-view="intro"]'),
    deck:    root.querySelector('[data-view="deck"]'),
    results: root.querySelector('[data-view="results"]'),
  };

  const deckEl           = root.querySelector('[data-deck]');
  const deckStageEl      = root.querySelector('.deck-stage');
  const counterCurrentEl = root.querySelector('[data-counter-current]');
  const counterTotalEl   = root.querySelector('[data-counter-total]');
  const favoritesGridEl  = root.querySelector('[data-favorites-grid]');
  const likesGridEl      = root.querySelector('[data-likes-grid]');
  const favoritesSection = root.querySelector('[data-favorites-section]');
  const likesSection     = root.querySelector('[data-likes-section]');
  const emptyEl          = root.querySelector('[data-results-empty]');
  const summaryEl        = root.querySelector('[data-results-summary]');
  const labelPassEl      = root.querySelector('[data-swipe-label="pass"]');
  const labelLikeEl      = root.querySelector('[data-swipe-label="like"]');

  /* ---------- INIT ---------- */
  function init() {
    loadCatalog();
    restoreState();
    bindActions();

    // Sync inCartIds with the real Shopify cart on load — so if the user
    // removed items in the cart drawer or another tab, our gold ✓ marks
    // accurately reflect what's really in their cart.
    syncWithShopifyCart();

    // Fetch community like counts from Supabase (anonymous aggregate data).
    // Non-blocking: deck renders first, counts appear once they arrive.
    fetchLikeCounts();

    // Re-sync whenever the user comes back to the tab (they may have
    // edited the cart elsewhere)
    document.addEventListener('visibilitychange', function(){
      if (!document.hidden) syncWithShopifyCart();
    });

    // Expose for theme cart events (defined in the Liquid section)
    window.CRYSTAL_SWIPE_RESYNC = syncWithShopifyCart;

    // Auto-start: skip the intro view entirely.
    // If there are no products, show the empty state from the results view.
    if (state.allProducts.length === 0) {
      renderResults();
      showView('results');
      return;
    }
    startDeck();
  }

  /* Pull the real Shopify cart and reconcile state.inCartIds with it.
     Updates any visible card buttons and re-renders the results view if shown. */
  function syncWithShopifyCart() {
    if (typeof window.SHOPIFY_CART_GET !== 'function') return;
    window.SHOPIFY_CART_GET().then(function(serverIds){
      if (!serverIds) return;

      // Replace local state with server truth
      state.inCartIds = new Set(serverIds);
      persistState();

      // Update any visible card buttons in the deck
      Array.from(deckEl.querySelectorAll('.card')).forEach(function(card){
        const pid = Number(card.dataset.productId);
        const favBtn = card.querySelector('[data-fav]');
        if (!favBtn) return;
        const wantInCart = state.inCartIds.has(pid);
        const isInCart = favBtn.classList.contains('is-in-cart');
        if (wantInCart && !isInCart) {
          favBtn.classList.add('is-in-cart');
          favBtn.setAttribute('disabled', '');
          favBtn.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 12l5 5 9-10"/></svg>';
        } else if (!wantInCart && isInCart) {
          favBtn.classList.remove('is-in-cart');
          favBtn.removeAttribute('disabled');
          favBtn.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.3"><path d="M3 5h2l2.5 11h11l2.5-8H7"/><circle cx="9" cy="20" r="1.4"/><circle cx="17" cy="20" r="1.4"/></svg>';
        }
      });

      // If the results view is currently visible, re-render it with new state
      if (views.results && views.results.classList.contains('is-active')) {
        renderResults();
      }
    });
  }

  /* ---------- COMMUNITY LIKE COUNTS (via Supabase) ----------
     Fetch current "X people loved this" numbers and apply to visible cards.
     The `LIKE_THRESHOLD` controls the minimum count needed to display the
     badge — lower numbers stay hidden so empty/new products don't look bad.
     Currently 0 = always show the badge, even for products with 0 likes. */
  const LIKE_THRESHOLD = 0;

  function getSupabaseConfig() {
    if (!root) return null;
    const url  = root.getAttribute('data-supabase-url');
    const key  = root.getAttribute('data-supabase-key');
    if (!url || !key || url.indexOf('REPLACE_') === 0) return null;
    return { url, key };
  }

  function fetchLikeCounts() {
    const cfg = getSupabaseConfig();
    if (!cfg) return; // not configured yet — silent, feature simply hidden
    if (state.allProducts.length === 0) return;

    // Get the IDs of products in this session deck only (not all 400 — wasteful)
    const ids = state.products.length
      ? state.products.map(p => p.id)
      : state.allProducts.slice(0, 50).map(p => p.id);
    if (!ids.length) return;

    // PostgREST `in.(...)` filter
    const url = cfg.url + '/rest/v1/product_likes'
      + '?select=product_id,like_count'
      + '&product_id=in.(' + ids.join(',') + ')';

    fetch(url, {
      headers: {
        'apikey': cfg.key,
        'Authorization': 'Bearer ' + cfg.key,
        'Accept': 'application/json'
      }
    })
    .then(function(r){ return r.ok ? r.json() : []; })
    .then(function(rows){
      (rows || []).forEach(function(row){
        state.likeCounts[row.product_id] = row.like_count;
      });
      // Update any cards already in the DOM
      paintLikeCounts();
    })
    .catch(function(err){ console.warn('[swipe] like-count fetch failed:', err); });
  }

  function incrementLike(productId) {
    const cfg = getSupabaseConfig();
    if (!cfg) return;
    if (state.likedThisSession.has(productId)) return; // dedupe
    state.likedThisSession.add(productId);

    fetch(cfg.url + '/rest/v1/rpc/increment_product_like', {
      method: 'POST',
      headers: {
        'apikey': cfg.key,
        'Authorization': 'Bearer ' + cfg.key,
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ p_product_id: productId })
    })
    .then(function(r){ return r.ok ? r.json() : null; })
    .then(function(newCount){
      if (typeof newCount === 'number') {
        state.likeCounts[productId] = newCount;
      }
    })
    .catch(function(){});
  }

  // Apply current likeCounts to all visible cards (in deck or results)
  function paintLikeCounts() {
    document.querySelectorAll('[data-like-badge]').forEach(function(el){
      const pid = Number(el.getAttribute('data-like-badge'));
      const count = state.likeCounts[pid] || 0;
      if (count >= LIKE_THRESHOLD) {
        el.textContent = '♥ ' + count + ' love this';
        el.style.display = '';
      } else {
        el.style.display = 'none';
      }
    });
  }

  function loadCatalog() {
    const dataEl = document.getElementById('swipe-data');
    if (!dataEl) return;
    try {
      state.allProducts = JSON.parse(dataEl.textContent.trim());
    } catch (e) {
      console.error('[swipe] Invalid product JSON', e);
      state.allProducts = [];
    }
  }

  /* Build the deck for this session:
     1. Exclude products the user has already seen (liked or passed) in the past
     2. Shuffle what remains
     3. If the user has exhausted the whole catalog, gracefully reset and show everything again
  */
  /*
    Build a fresh session deck from the available products.

    Strategy (assuming a catalog of e.g. 400 products and deck_size = 24):
     1. Partition: allProducts → [unseen] + [recentlySeen]
     2. Shuffle both partitions.
     3. Take `deckSize` items, preferring unseen. If unseen is smaller than
        deckSize, top up from recentlySeen.
     4. If the user has exhausted the catalog entirely, clear seenIds but
        push the ones they JUST saw to the bottom of the new deck so the
        repeat feels less obvious.

    This means:
      • Every "Discover again" shows a fresh 24 randomly picked from the 400.
      • Users can't see the same product within the seen-expiry window (3 days).
      • After 3 days, products become eligible again naturally.
  */
  function buildSessionDeck() {
    const deckSize = getDeckSize();
    const catalog = state.allProducts.slice();

    if (catalog.length === 0) {
      state.products = [];
      return;
    }

    const unseen = catalog.filter(p => !state.seenIds.has(p.id));

    if (unseen.length >= deckSize) {
      // Happy path: plenty of unseen products. Pick `deckSize` randomly.
      state.products = shuffle(unseen).slice(0, deckSize);
      return;
    }

    // Not enough unseen to fill a deck — top up from recently-seen products.
    // This happens when the user has churned through most of the catalog
    // within the retention window.
    const recentlySeen = catalog.filter(p => state.seenIds.has(p.id));

    if (unseen.length === 0 && recentlySeen.length > 0) {
      // Fully exhausted — reset seen history so browsing feels fresh,
      // but remember what was just shown so we can put it last in the deck.
      const justShown = new Set(state.seenIds);
      state.seenIds = new Set();
      persistState();
      const shuffled = shuffle(catalog);
      const fresh   = shuffled.filter(p => !justShown.has(p.id));
      const repeats = shuffled.filter(p =>  justShown.has(p.id));
      state.products = fresh.concat(repeats).slice(0, deckSize);
      return;
    }

    // Partial: use all unseen, then top up with shuffled recently-seen.
    const freshDeck = shuffle(unseen);
    const topUp     = shuffle(recentlySeen).slice(0, deckSize - freshDeck.length);
    state.products  = freshDeck.concat(topUp);
  }

  // Read deck size from the section's data-deck-size attribute, with a safe fallback.
  function getDeckSize() {
    const attr = root && root.getAttribute && root.getAttribute('data-deck-size');
    const n = parseInt(attr, 10);
    if (!isFinite(n) || n < 4) return DEFAULT_DECK_SIZE;
    return n;
  }

  // Fisher-Yates shuffle — unbiased, in-place
  function shuffle(arr) {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }

  // Hide both swipe direction labels. Used everywhere we need to guarantee
  // they're cleared (drag end, cancel, card promotion, button action, etc).
  function hideSwipeLabels() {
    if (labelPassEl) labelPassEl.style.opacity = 0;
    if (labelLikeEl) labelLikeEl.style.opacity = 0;
  }

  /* ---------- VIEW SWITCHING ---------- */
  function showView(name) {
    Object.values(views).forEach(v => {
      if (v) v.classList.remove('is-active');
    });
    if (views[name]) views[name].classList.add('is-active');
    // Scroll to top — use the legacy form for maximum browser compatibility.
    try { window.scrollTo(0, 0); } catch(_) {}
    try {
      if (root && root.scrollIntoView) {
        root.scrollIntoView({ block: 'start' });
      }
    } catch(_) {}
  }

  /* ---------- ACTIONS ---------- */
  function bindActions() {
    root.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-action]');
      if (!btn) return;
      const action = btn.dataset.action;

      if (action === 'start')    { startDeck(); }
      if (action === 'restart')  { resetAll(); startDeck(); }
      if (action === 'pass')     { programmaticSwipe(-1); }
      if (action === 'like')     { programmaticSwipe(1); }
      if (action === 'favorite') { addCurrentToCart(); } // now: the ✦ button adds to cart
      if (action === 'viewAll')  { window.location.href = '/collections/all'; }
      if (action === 'addCart')  {
        const id = Number(btn.dataset.productId);
        addToCart(id);
      }
    });
  }

  /* ---------- DECK ---------- */
  function startDeck() {
    // Fresh session: new shuffle, reset per-run likes/passes, but preserve favorites + seenIds
    buildSessionDeck();
    state.index = 0;
    state.liked = [];
    state.passed = [];
    counterTotalEl.textContent = state.products.length;

    if (state.products.length === 0) {
      // Nothing to show — shouldn't happen because buildSessionDeck resets, but guard anyway
      renderResults();
      showView('results');
      return;
    }

    renderDeck();
    updateCounter();
    showView('deck');
  }

  function renderDeck() {
    deckEl.innerHTML = '';
    const remaining = state.products.slice(state.index, state.index + STACK_SIZE);
    // Render back-to-front so the topmost is last in DOM (highest z-index)
    remaining.slice().reverse().forEach((product, reverseIdx) => {
      const depth = remaining.length - 1 - reverseIdx; // 0 = top card
      const card = buildCard(product, depth);
      deckEl.appendChild(card);
    });
  }

  function buildCard(product, depth) {
    const card = document.createElement('article');
    card.className = 'card';
    if (depth === 1) card.classList.add('is-next');
    if (depth >= 2)  card.classList.add('is-next-2');
    card.dataset.productId = product.id;

    const tagsHTML = (product.tags || [])
      .slice(0, 3)
      .map(t => `<span class="card-tag">${escapeHTML(t)}</span>`)
      .join('');

    const inCart = state.inCartIds.has(product.id);
    const likeCount = state.likeCounts[product.id] || 0;
    const showBadge = likeCount >= LIKE_THRESHOLD;

    card.innerHTML = `
      <div class="card-media">
        ${tagsHTML ? `<div class="card-tags">${tagsHTML}</div>` : ''}
        <button class="card-fav ${inCart ? 'is-in-cart' : ''}"
                data-fav
                aria-label="${inCart ? 'Added to cart' : 'Add to cart'}"
                ${inCart ? 'disabled' : ''}>
          ${inCart
            ? `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 12l5 5 9-10"/></svg>`
            : `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.3"><path d="M3 5h2l2.5 11h11l2.5-8H7"/><circle cx="9"  cy="20" r="1.4"/><circle cx="17" cy="20" r="1.4"/></svg>`
          }
        </button>
        <img src="${escapeAttr(product.image)}" alt="${escapeAttr(product.title)}" loading="lazy" draggable="false">
      </div>
      <div class="card-info">
        <h2 class="card-title">${escapeHTML(product.title)}</h2>
        <p class="card-subtitle">${escapeHTML(product.subtitle || '')}</p>
        <span class="card-like-badge"
              data-like-badge="${product.id}"
              style="${showBadge ? '' : 'display:none'}">
          ${showBadge ? '♥ ' + likeCount + ' love this' : ''}
        </span>
        <div class="card-footer">
          <span class="card-price">${escapeHTML(product.price)}</span>
          <a class="card-view" href="${escapeAttr(product.url)}" target="_blank" rel="noopener">View piece →</a>
        </div>
      </div>
    `;

    // Only the top card is interactive for swiping
    if (depth === 0) {
      attachTopCardHandlers(card, product);
      card.dataset.dragBound = '1';
    }

    return card;
  }

  function attachTopCardHandlers(card, product) {
    enableDrag(card);
    const favBtn = card.querySelector('[data-fav]');
    if (favBtn) {
      favBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        if (state.inCartIds.has(product.id)) return; // already added
        addProductToCart(product, favBtn);
      });
    }
    const viewLink = card.querySelector('.card-view');
    if (viewLink) viewLink.addEventListener('click', e => e.stopPropagation());
  }

  /* ---------- DRAG ---------- */
  const DIRECTION_LOCK_DIST = 8; // px — decide horizontal vs vertical after this much movement

  // Gentle haptic blip on supported devices (iOS 13+ / Android Chrome)
  const haptic = (ms = 10) => {
    if (navigator.vibrate) { try { navigator.vibrate(ms); } catch(_){} }
  };

  function enableDrag(card) {
    let startX = 0, startY = 0, currentX = 0, currentY = 0;
    let dragging = false;     // true when horizontal swipe is active
    let pending = false;      // true from pointerdown until direction is locked
    let startTime = 0;
    let pointerId = null;
    let rafPending = false;
    let hapticAtThreshold = false;

    const applyTransform = () => {
      rafPending = false;
      const rotate = (currentX / 300) * MAX_ROTATION;
      card.style.transform =
        `translate3d(${currentX}px, ${currentY * 0.4}px, 0) rotate(${rotate}deg)`;

      // Fade in the direction label proportional to drag distance.
      // Starts showing at ~24px, fully visible at SWIPE_THRESHOLD.
      const progress = Math.max(0, (Math.abs(currentX) - 24) / (SWIPE_THRESHOLD - 24));
      const opacity = Math.min(1, progress);
      if (currentX > 0) {
        if (labelLikeEl) labelLikeEl.style.opacity = opacity;
        if (labelPassEl) labelPassEl.style.opacity = 0;
      } else if (currentX < 0) {
        if (labelPassEl) labelPassEl.style.opacity = opacity;
        if (labelLikeEl) labelLikeEl.style.opacity = 0;
      } else {
        if (labelPassEl) labelPassEl.style.opacity = 0;
        if (labelLikeEl) labelLikeEl.style.opacity = 0;
      }

      // Subtle haptic when crossing the swipe commit line (once per drag)
      if (!hapticAtThreshold && Math.abs(currentX) >= SWIPE_THRESHOLD) {
        hapticAtThreshold = true;
        haptic(8);
      }
      if (hapticAtThreshold && Math.abs(currentX) < SWIPE_THRESHOLD - 10) {
        hapticAtThreshold = false;
      }
    };

    const cancelDrag = () => {
      pending = false;
      dragging = false;
      card.classList.remove('is-dragging');
      if (deckStageEl) deckStageEl.classList.remove('is-dragging');
      hideSwipeLabels();
      try { card.releasePointerCapture(pointerId); } catch(_){}
    };

    const onDown = (e) => {
      if (e.target.closest('[data-fav], .card-view')) return;
      pending = true;
      dragging = false;
      pointerId = e.pointerId;
      try { card.setPointerCapture(pointerId); } catch(_) {}
      startX = e.clientX;
      startY = e.clientY;
      currentX = 0; currentY = 0;
      startTime = performance.now();
      hapticAtThreshold = false;
    };

    const onMove = (e) => {
      if (!pending && !dragging) return;
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;

      // --- direction-lock phase ---
      if (pending && !dragging) {
        const dist = Math.hypot(dx, dy);
        if (dist < DIRECTION_LOCK_DIST) return;
        // decide intent
        if (Math.abs(dx) > Math.abs(dy)) {
          // horizontal → commit to drag
          dragging = true;
          pending = false;
          card.classList.add('is-dragging');
          // Mark stage as dragging too, so labels lose their transition
          // and track the finger instantly
          if (deckStageEl) deckStageEl.classList.add('is-dragging');
        } else {
          // vertical → release capture so the page can scroll
          cancelDrag();
          return;
        }
      }

      if (!dragging) return;
      currentX = dx;
      currentY = dy;
      if (!rafPending) {
        rafPending = true;
        requestAnimationFrame(applyTransform);
      }
    };

    const onUp = () => {
      if (!pending && !dragging) return;
      if (pending && !dragging) { pending = false; return; }
      dragging = false;
      card.classList.remove('is-dragging');
      if (deckStageEl) deckStageEl.classList.remove('is-dragging');
      try { card.releasePointerCapture(pointerId); } catch (_) {}

      const dt = performance.now() - startTime;
      const velocity = Math.abs(currentX) / Math.max(dt, 1);
      const passesThreshold = Math.abs(currentX) > SWIPE_THRESHOLD;
      const passesVelocity  = velocity > VELOCITY_THRESHOLD && Math.abs(currentX) > 40;

      // Hide direction labels (smoothly via CSS transition now that
      // deck-stage no longer has the .is-dragging class)
      hideSwipeLabels();

      if (passesThreshold || passesVelocity) {
        haptic(14); // commit blip
        completeSwipe(card, currentX > 0 ? 1 : -1, velocity);
      } else {
        // spring back
        card.style.transform = 'translate3d(0,0,0) rotate(0deg)';
      }
    };

    card.addEventListener('pointerdown',  onDown);
    card.addEventListener('pointermove',  onMove);
    card.addEventListener('pointerup',    onUp);
    card.addEventListener('pointercancel', onUp);
    card.addEventListener('lostpointercapture', () => {
      if (dragging || pending) onUp();
    });
  }

  function programmaticSwipe(direction) {
    // top card = the last non-helper card in the DOM
    const cards = Array.from(deckEl.querySelectorAll('.card:not(.is-flying):not(.is-next):not(.is-next-2)'));
    const topCard = cards[cards.length - 1];
    if (!topCard) return;
    completeSwipe(topCard, direction, 0);
  }

  function completeSwipe(card, direction, velocity) {
    const product = state.products.find(p => p.id === Number(card.dataset.productId));
    if (!product) return;

    // register
    if (direction === 1) {
      state.liked.push(product);
      // Anonymously increment the global "people who loved this" counter
      incrementLike(product.id);
    } else {
      state.passed.push(product);
    }
    // Remember across sessions so this product won't reappear next time
    state.seenIds.add(product.id);

    // mark as flying so promoteStack skips it
    card.classList.add('is-flying');
    card.classList.remove('is-dragging');
    // Ensure stage is no longer "dragging" — allows label fade-out transition
    if (deckStageEl) deckStageEl.classList.remove('is-dragging');
    // Hide swipe labels immediately (belt-and-suspenders: onUp also does this)
    hideSwipeLabels();

    // animate off-screen — scale exit speed with fling velocity
    const offX = direction * (window.innerWidth * 0.9 + 120);
    const offRot = direction * 22;
    const duration = Math.max(260, 450 - (velocity || 0) * 120);
    card.style.transition = `transform ${duration}ms var(--ease-out), opacity ${duration}ms var(--ease-out)`;
    // next frame — ensures the transition actually runs
    requestAnimationFrame(() => {
      card.style.transform = `translate3d(${offX}px, 60px, 0) rotate(${offRot}deg)`;
      card.style.opacity = '0';
    });

    // promote the remaining cards immediately — don't wait for transitionend
    promoteStack();

    // cleanup after animation
    setTimeout(() => {
      if (card.parentNode) card.parentNode.removeChild(card);
      advanceAfterSwipe();
    }, duration + 30);

    persistState();
  }

  function promoteStack() {
    // Only count cards that aren't flying away
    const remaining = Array.from(deckEl.querySelectorAll('.card:not(.is-flying)'));
    // DOM order: bottom-first, so the last element is the top card
    // remaining[remaining.length - 1] = new top
    // remaining[remaining.length - 2] = new is-next
    // remaining[0..n-3]              = is-next-2
    const n = remaining.length;
    remaining.forEach((c, idx) => {
      c.classList.remove('is-next', 'is-next-2');
      // clear any leftover inline transform/opacity from previous states
      c.style.transform = '';
      c.style.opacity = '';

      if (idx === n - 1) {
        // new top card — attach drag if not already
        if (!c.dataset.dragBound) {
          const product = state.products.find(p => p.id === Number(c.dataset.productId));
          if (product) attachTopCardHandlers(c, product);
          c.dataset.dragBound = '1';
        }
      } else if (idx === n - 2) {
        c.classList.add('is-next');
      } else {
        c.classList.add('is-next-2');
      }
    });
  }

  function advanceAfterSwipe() {
    state.index += 1;
    updateCounter();

    if (state.index >= state.products.length) {
      // wait until last card finishes flying before showing results
      renderResults();
      showView('results');
      return;
    }

    // Append ONE new card at the back of the stack (if one exists)
    const nextToAdd = state.products[state.index + STACK_SIZE - 1];
    if (nextToAdd) {
      const newCard = buildCard(nextToAdd, 2); // goes in as is-next-2
      // insert at the beginning so DOM order = bottom-first, last = top
      deckEl.insertBefore(newCard, deckEl.firstChild);
    }
  }

  function updateCounter() {
    counterCurrentEl.textContent = Math.min(state.index + 1, state.products.length);
  }

  /* ---------- CART ---------- */
  function addProductToCart(product, btnEl) {
    if (!product || state.inCartIds.has(product.id)) return;

    // Optimistic UI — mark in-cart immediately
    state.inCartIds.add(product.id);
    persistState();

    // Anonymously increment the global "people who loved this" counter.
    // Dedupe: incrementLike() itself checks likedThisSession, so swiping right
    // and then adding to cart on the same product still only counts as +1.
    incrementLike(product.id);

    // Update the ✦ → ✓ state on the button that triggered this
    if (btnEl) {
      btnEl.classList.add('is-in-cart');
      btnEl.setAttribute('aria-label', 'Added to cart');
      btnEl.setAttribute('disabled', '');
      btnEl.innerHTML =
        `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 12l5 5 9-10"/></svg>`;
    }

    // Route to Shopify (or mock fallback)
    if (typeof window.SHOPIFY_CART_ADD === 'function') {
      try { window.SHOPIFY_CART_ADD(product); } catch(_){}
    } else {
      console.log('[swipe] add to cart →', product);
    }

    haptic(16);
  }

  function addCurrentToCart() {
    const cards = Array.from(deckEl.querySelectorAll('.card:not(.is-flying):not(.is-next):not(.is-next-2)'));
    const topCard = cards[cards.length - 1];
    if (!topCard) return;
    const favBtn = topCard.querySelector('[data-fav]');
    if (favBtn && !favBtn.hasAttribute('disabled')) favBtn.click();
  }

  /* ---------- RESULTS ---------- */
  function renderResults() {
    // Surface the user's cart picks separately from likes
    const inCartProducts = state.allProducts.filter(p => state.inCartIds.has(p.id));
    // Likes that aren't already in cart (avoid showing the same product twice)
    const likedOnly = state.liked.filter(p => !state.inCartIds.has(p.id));

    if (favoritesGridEl)  favoritesGridEl.innerHTML = inCartProducts.map(p => resultCardHTML(p, true)).join('');
    if (likesGridEl)      likesGridEl.innerHTML     = likedOnly.map(p => resultCardHTML(p, false)).join('');

    if (favoritesSection) favoritesSection.classList.toggle('is-empty', inCartProducts.length === 0);
    if (likesSection)     likesSection.classList.toggle('is-empty',     likedOnly.length === 0);

    const totalSelected = inCartProducts.length + likedOnly.length;
    if (totalSelected === 0) {
      if (emptyEl)   emptyEl.hidden = false;
      if (summaryEl) summaryEl.textContent = '';
    } else {
      if (emptyEl) emptyEl.hidden = true;
      const cartPart = inCartProducts.length
        ? `${inCartProducts.length} added to cart`
        : '';
      const keptPart = likedOnly.length
        ? `${likedOnly.length} kept for later`
        : '';
      if (summaryEl) summaryEl.textContent = [cartPart, keptPart].filter(Boolean).join(' · ');
    }
  }

  function resultCardHTML(product, alreadyInCart) {
    const tag = (product.tags && product.tags[0]) || '';
    const cartBtn = alreadyInCart
      ? `<button class="r-btn r-btn-primary is-in-cart" disabled>✓ In cart</button>`
      : `<button class="r-btn r-btn-primary" data-action="addCart" data-product-id="${product.id}">Add to cart</button>`;
    return `
      <article class="result-card">
        <a class="r-media" href="${escapeAttr(product.url)}">
          <img src="${escapeAttr(product.image)}" alt="${escapeAttr(product.title)}" loading="lazy">
        </a>
        <div class="r-body">
          <h4 class="r-title">${escapeHTML(product.title)}</h4>
          <p class="r-price">${escapeHTML(product.price)}${tag ? ' · ' + escapeHTML(tag) : ''}</p>
          <div class="r-actions">
            <a class="r-btn" href="${escapeAttr(product.url)}">View</a>
            ${cartBtn}
          </div>
        </div>
      </article>
    `;
  }

  /* ---------- SHOPIFY HOOK (called from result-page "Add to cart" buttons) ---------- */
  function addToCart(productId) {
    const product = state.allProducts.find(p => p.id === productId)
                 || state.products.find(p => p.id === productId);
    if (!product) return;
    if (state.inCartIds.has(product.id)) return;

    state.inCartIds.add(product.id);
    persistState();

    // Re-render result page so the button updates to "✓ In cart"
    renderResults();

    if (typeof window.SHOPIFY_CART_ADD === 'function') {
      try { window.SHOPIFY_CART_ADD(product); } catch(_){}
    } else {
      console.log('[swipe] add to cart →', product);
    }
  }

  /* ---------- PERSISTENCE ---------- */
  function persistState() {
    try {
      const payload = {
        version: 3,
        inCart:   Array.from(state.inCartIds),
        inCartTs: Date.now(),
        seen:     Array.from(state.seenIds),
        seenTs:   Date.now(),
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch (_) { /* storage unavailable, ignore */ }
  }

  function restoreState() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const saved = JSON.parse(raw);
      if (!saved) return;

      // In-cart state — clear it after 24 hours so a user who abandons their
      // cart can still add again next time (most Shopify carts expire in 14 days
      // anyway; if it's still there, the server will deduplicate)
      const cartExpiryMs = 24 * 60 * 60 * 1000;
      const cartAge = Date.now() - (saved.inCartTs || 0);
      if (Array.isArray(saved.inCart) && cartAge < cartExpiryMs) {
        state.inCartIds = new Set(saved.inCart);
      }

      // Seen history — forget after SEEN_EXPIRY_DAYS so browsing feels fresh again
      const seenExpiryMs = SEEN_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
      const seenAge = Date.now() - (saved.seenTs || 0);
      if (Array.isArray(saved.seen) && seenAge < seenExpiryMs) {
        state.seenIds = new Set(saved.seen);
      }
    } catch (_) {}
  }

  function resetAll() {
    state.index = 0;
    state.liked = [];
    state.passed = [];
    // preserve inCart + seenIds across restarts within a session
  }

  /* ---------- UTIL ---------- */
  function escapeHTML(str) {
    return String(str ?? '').replace(/[&<>"']/g, c => ({
      '&':'&amp;', '<':'&lt;', '>':'&gt;', '"':'&quot;', "'":'&#39;'
    }[c]));
  }
  function escapeAttr(str) { return escapeHTML(str); }

  /* ---------- GO ---------- */
  init();
})();